import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import SearchBox from "../../../../Components/SearchBox";
import { Formik } from "formik";

import { useQuery } from "@tanstack/react-query";
import { GuideSearchBarInitialValues } from "../../constant";
import {
  ErrorToast,
  SuccessToast,
  Confirmation,
} from "../../../../Utils/SweetAlert";
import {
  useMutateDeleteGuide,
  useMutateDragDropGuide,
} from "../../hooks/useApiHook";
import { DeleteGuide, GetGuideList } from "../../../../Api/guide";
import { IoEye } from "react-icons/io5";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { EditButton, DeleteButton, AddButton } from "../../../../Components";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import NameInitial from "../../../../Components/NameInitial/Nameinitial";

const GuideList = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  const deleteGuide = useMutateDeleteGuide();
  const dragdropGuide = useMutateDragDropGuide();

  const [guideProList, setGuideProList] = useState(null);

  const {
    isFetching: isGuideLoading,
    data: guideProductList,
    refetch,
  } = useQuery({
    queryKey: ["get-guide-list"],
    queryFn: () => GetGuideList(formRef?.current?.values?.search || ""),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server error.");
    },
  });

  useEffect(() => {
    if (guideProductList) {
      setGuideProList(guideProductList);
    }
  }, [guideProductList]);

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const { source, destination } = result;

    const items = Array.from(guideProList.data);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);
    setGuideProList({ ...guideProList, data: items });

    dragdropGuide.mutate(
      {
        guideProductId: reorderedItem._id,
        newOrder: destination.index,
      },
      {
        onSuccess: () => {
          // SuccessToast(response?.message || "Drag And Drop Successful.");
          refetch();
        },
        onError: (error) => {
          ErrorToast(error?.response?.data?.message || "Failed To Reorder.");
        },
      }
    );
  };

  return (
    <>
      <Helmet>
        <title>OVE | Admin Guide Product</title>
      </Helmet>

      <div className="page-wrapper guide-product">
        <div className="content container-fluid">
          <div className="admin-wrapper">
            <div className="page-header">
              <div className="col">
                <h1 className="page-title">Guide Product</h1>
              </div>
              <div className="col-12 col-md-3 col-lg-2 px-2">
                <Formik
                  initialValues={GuideSearchBarInitialValues}
                  innerRef={formRef}
                >
                  {() => (
                    <form>
                      <SearchBox refetch={refetch} setPage={() => { }} />
                    </form>
                  )}
                </Formik>
              </div>
              <div>
                <AddButton
                  to="/admin/add-guide"
                  permission="create-guide-product"
                />
              </div>
            </div>
            <div className="col-sm-12 table-width">
              <div className="cards-table card-table">
                <div className="card-body">
                  <div className="table-responsive guide-product-table custom-pagination table-no-padding">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <table className="table datatable table-manager mb-0" style={{ width: '100%' }}>
                        <thead className="thead-light">
                          <tr>
                            <th className="col-icon text-start" style={{ width: '20%' }}>Icon</th>
                            <th className="col-name text-start" style={{ width: '40%' }}>Name</th>
                            <th className="col-action text-center" style={{ width: '40%' }}>Action</th>
                          </tr>
                        </thead>

                        {isGuideLoading && (
                          <tbody>
                            <tr>
                              <td align="center" colSpan={3}>
                                <div className="d-flex justify-content-center align-items-center py-4">
                                  <div className="spinner-border loader-theme" role="status" />
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        )}

                        {!isGuideLoading && (
                          <Droppable droppableId="droppable">
                            {(provided) => (
                              <tbody
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {guideProList?.data.map((item, index) => (
                                  <Draggable
                                    key={item._id}
                                    draggableId={item._id}
                                    index={index}
                                  >
                                    {(provided) => (
                                      <tr
                                        key={index}
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className="table disable-row"
                                      >
                                        <td className="col-icon text-start">
                                          <div className="drag-content">
                                            {item?.image && item?.image.trim() !== "" ? (
                                              <img
                                                src={item?.image}
                                                alt="img"
                                                className="list-image-news"
                                                style={{ boxShadow: 'none' }}
                                                onError={(e) => {
                                                  e.currentTarget.src = "/images/defaultImage.jpg"; // fallback image path
                                                }}
                                              />
                                            ) : (
                                              <NameInitial
                                                name={item.name}
                                                type="image"
                                              />
                                            )}
                                          </div>
                                        </td>
                                        <td className="col-name text-start">
                                          <span className="guide-cell-ellipsis">{item?.name || "-"}</span>
                                        </td>
                                        <td className="guide-td-padding-remove col-action text-center">
                                          <EditButton
                                            to="/admin/edit-guide"
                                            state={{ id: item?._id }}
                                            permission="edit-guide-product"
                                          />
                                          <DeleteButton
                                            onDelete={() => {
                                              Confirmation("Are you sure you want to delete guide?").then((result) => {
                                                if (result.isConfirmed) {
                                                  deleteGuide.mutate(item?._id, {
                                                    onSuccess: (response) => {
                                                      SuccessToast(response?.message || "Delete successful.");
                                                      refetch();
                                                    },
                                                    onError: (error) => {
                                                      ErrorToast(error?.response?.data?.message || "Server error.");
                                                    },
                                                  });
                                                }
                                              });
                                            }}
                                            permission="delete-guide-product"
                                          />
                                          <Tooltip
                                            title="View"
                                            className="btn btn-sm btn-white text-success insight-stickey-actions-btn"
                                            onClick={() =>
                                              navigate(
                                                "/admin/guide-detail",
                                                {
                                                  state: {
                                                    id: item?._id,
                                                  },
                                                }
                                              )
                                            }
                                          >
                                            <IconButton>
                                              <IoEye className="text-secondary icon-color p-0" />
                                            </IconButton>
                                          </Tooltip>
                                        </td>
                                      </tr>
                                    )}
                                  </Draggable>
                                ))}
                                {guideProList?.data?.length === 0 && (
                                  <tr>
                                    <td className="text-center" colSpan={3}>
                                      No Records Found.
                                    </td>
                                  </tr>
                                )}
                                {provided.placeholder}
                              </tbody>
                            )}
                          </Droppable>
                        )}
                      </table>
                    </DragDropContext>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GuideList;
